// src/components/financeOfficer/RequisitionsTable.jsx

import React, { useState, useMemo, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaUserCircle, FaEye, FaCheckCircle, FaExchangeAlt } from "react-icons/fa";
import { FiSearch, FiDownload, FiFileText, FiFilter, FiChevronLeft, FiChevronRight, FiGrid } from "react-icons/fi";
import ViewRequisitionModal from "./ViewRequisitionModal";

const logo = "/logo.jpeg";

const getStatusColor = (status) => {
    switch (status) {
        case "Approved":
            return "bg-green-100 text-green-800";
        case "Rejected":
            return "bg-red-100 text-red-800";
        case "Pending":
        default:
            return "bg-yellow-100 text-yellow-800";
    }
};

const mapRequisitionToExportRow = (req) => ({
    "Requisition ID": req._id,
    "Requesting Officer": `${req.officer?.firstName} ${req.officer?.lastName}` || "-",
    "Items/Description": req.items,
    Amount: req.amount,
    Department: req.department,
    "Date Created": new Date(req.createdAt).toLocaleDateString(),
    Status: req.status,
    Purpose: req.purpose || "N/A",
});

export default function RequisitionsTable({ requisitions = [], onApprove, loading = false }) {
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const requisitionsPerPage = 10;
    
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedRequisition, setSelectedRequisition] = useState(null);

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const filteredRequisitions = useMemo(() => {
        const q = search.toLowerCase();
        return requisitions.filter((req) =>
            [req.officer?.firstName, req.officer?.lastName, req.items, req.department, req.amount]
                .filter(Boolean)
                .some((field) => String(field).toLowerCase().includes(q))
        );
    }, [requisitions, search]);

    const totalPages = Math.ceil(filteredRequisitions.length / requisitionsPerPage);

    const currentRequisitions = useMemo(() => {
        const start = (currentPage - 1) * requisitionsPerPage;
        return filteredRequisitions.slice(start, start + requisitionsPerPage);
    }, [filteredRequisitions, currentPage]);

    useEffect(() => setCurrentPage(1), [search]);

    const openViewModal = (req) => {
        setSelectedRequisition(req);
        setViewModalOpen(true);
    };

    const handleExport = (format) => {
        setExportDropdownOpen(false);
        const exportData = filteredRequisitions.map(mapRequisitionToExportRow);

        if (!exportData.length) {
            alert("No requisitions to export!");
            return;
        }

        switch (format) {
            case "excel":
                exportToExcel(exportData);
                break;
            case "csv":
                exportToCSV(exportData);
                break;
            case "pdf":
                exportToPDF(exportData);
                break;
            default:
                alert("Unsupported export format");
        }
    };
    
    const exportToExcel = (exportData) => {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Requisitions");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(dataBlob, "requisitions_report.xlsx");
    };

    const exportToCSV = (exportData) => {
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(csvBlob, "requisitions_report.csv");
    };

    const exportToPDF = (exportData) => {
        const doc = new jsPDF("l", "pt", "a4");
        const img = new Image();
        img.src = logo;
        const columns = Object.keys(exportData[0]);
    
        img.onload = () => {
            const margin = 40;
            const pageWidth = doc.internal.pageSize.width;
            const logoWidth = 60;
            const logoHeight = img.height * (logoWidth / img.width);
            
            doc.addImage(img, 'JPEG', margin, margin, logoWidth, logoHeight);
            doc.setFontSize(24);
            doc.setTextColor(51, 51, 51);
            doc.text("Requisitions Report", pageWidth / 2, margin + 20, { align: 'center' });
            
            doc.setFontSize(12);
            doc.setTextColor(102, 102, 102);
            doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, margin + 40, { align: 'center' });
            
            doc.setDrawColor(220, 220, 220);
            doc.line(margin, margin + 60, pageWidth - margin, margin + 60);
    
            autoTable(doc, {
                startY: margin + 70,
                head: [columns],
                body: exportData.map((row) => columns.map((col) => row[col])),
                theme: "grid",
                styles: { fontSize: 8, cellPadding: 3, textColor: [51, 51, 51] },
                headStyles: { fillColor: [240, 240, 240], textColor: [51, 51, 51], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [255, 255, 255] },
                margin: { top: 70, left: margin, right: margin },
                didDrawPage: (data) => {
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(10);
                    doc.setTextColor(150);
                    doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.height - 20, { align: 'right' });
                },
            });
            doc.save("requisitions_report.pdf");
        };
        img.onerror = () => {
            console.error("Failed to load logo image.");
            const docFallback = new jsPDF("l", "pt", "a4");
            autoTable(docFallback, { head: [columns], body: exportData.map(row => Object.values(row)) });
            docFallback.save("requisitions_report.pdf");
        };
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setExportDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div>
            <div className="mt-8 bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Search & Export Controls */}
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="relative flex-1 max-w-xl">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiSearch className="text-gray-400" />
                            </div>
                            <input
                                type="search"
                                placeholder="Search requisitions by officer, items, or department"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                            />
                        </div>
                        
                        <div className="flex items-center gap-4">
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <FiFilter className="text-orange-500" />
                                <span>{filteredRequisitions.length} records found</span>
                            </div>
                            
                            <div className="relative inline-block text-left" ref={dropdownRef}>
                                <button
                                    onClick={() => setExportDropdownOpen((open) => !open)}
                                    type="button"
                                    className="inline-flex items-center justify-center w-full rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-white shadow-sm hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all"
                                >
                                    <FiDownload className="mr-2 -ml-1 h-5 w-5" />
                                    Export
                                    <svg className="ml-2 -mr-1 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                                    </svg>
                                </button>
                                {exportDropdownOpen && (
                                    <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50 focus:outline-none">
                                        <div className="py-1">
                                            {["excel", "csv", "pdf"].map((format) => (
                                                <button
                                                    key={format}
                                                    onClick={() => handleExport(format)}
                                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700"
                                                >
                                                    <FiFileText className="mr-2 h-4 w-4" />
                                                    Export as {format.toUpperCase()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                    </div>
                )}

                {/* Desktop Table */}
                {!loading && (
                    <>
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Requesting Officer</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Items</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Amount</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Department</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Status</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-orange-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {currentRequisitions.length > 0 ? (
                                        currentRequisitions.map((req) => (
                                            <tr key={req._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10">
                                                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                                <FaUserCircle className="h-6 w-6 text-gray-500" />
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">{req.officer?.firstName} {req.officer?.lastName}</div>
                                                            <div className="text-sm text-gray-500">{req.officer?.email || ""}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900 max-w-xs truncate">{req.items}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 font-medium">{req.amount} SSP</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{req.department}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(req.status)}`}>
                                                        {req.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <button
                                                            onClick={() => openViewModal(req)}
                                                            className="text-orange-600 hover:text-orange-900 flex items-center"
                                                        >
                                                            <FaEye className="mr-1" /> View
                                                        </button>
                                                        {req.status === "Pending" && (
                                                            <button 
                                                                onClick={() => onApprove(req._id)}
                                                                className="text-green-600 hover:text-green-900 flex items-center"
                                                                aria-label={`Approve requisition ${req._id}`}
                                                            >
                                                                <FaCheckCircle className="mr-1" /> Approve
                                                            </button>
                                                        )}
                                                        {req.status === "Approved" && (
                                                            <button 
                                                                disabled
                                                                className="text-gray-500 flex items-center cursor-not-allowed"
                                                            >
                                                                <FaCheckCircle className="mr-1" /> Approved
                                                            </button>
                                                        )}
                                                        {req.status !== "Approved" && (
                                                            <button
                                                                className="text-gray-600 hover:text-gray-900 flex items-center"
                                                                aria-label={`Forward requisition ${req._id}`}
                                                            >
                                                                <FaExchangeAlt className="mr-1" /> Forward
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <FiGrid className="h-12 w-12 text-gray-400" />
                                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No requisitions found</h3>
                                                    <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="md:hidden p-4 space-y-4">
                            {currentRequisitions.length > 0 ? (
                                currentRequisitions.map((req) => (
                                    <div key={req._id} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                        <div className="px-4 py-5 sm:p-6">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                            <FaUserCircle className="h-6 w-6 text-gray-500" />
                                                        </div>
                                                    </div>
                                                    <div className="ml-3">
                                                        <h3 className="text-lg font-medium text-gray-900">{req.officer?.firstName} {req.officer?.lastName}</h3>
                                                        <p className="text-sm text-gray-500">{req.officer?.email || ""}</p>
                                                    </div>
                                                </div>
                                                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(req.status)}`}>
                                                    {req.status}
                                                </span>
                                            </div>
                                            <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Items</dt>
                                                    <dd className="mt-1 text-sm text-gray-900 truncate">{req.items}</dd>
                                                </div>
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Amount</dt>
                                                    <dd className="mt-1 text-sm text-gray-900 font-medium">{req.amount} SSP</dd>
                                                </div>
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Department</dt>
                                                    <dd className="mt-1 text-sm text-gray-900">{req.department}</dd>
                                                </div>
                                            </dl>
                                            <div className="mt-6 flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => openViewModal(req)}
                                                    className="flex-1 min-w-[100px] inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    <FaEye className="mr-2" />
                                                    View
                                                </button>
                                                {req.status === "Pending" && (
                                                    <button 
                                                        onClick={() => onApprove(req._id)}
                                                        className="flex-1 min-w-[100px] inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                                                    >
                                                        <FaCheckCircle className="mr-2" />
                                                        Approve
                                                    </button>
                                                )}
                                                {req.status === "Approved" && (
                                                    <button 
                                                        disabled
                                                        className="flex-1 min-w-[100px] inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-500 bg-gray-100 cursor-not-allowed"
                                                    >
                                                        <FaCheckCircle className="mr-2" />
                                                        Approved
                                                    </button>
                                                )}
                                                {req.status !== "Approved" && (
                                                    <button
                                                        className="flex-1 min-w-[100px] inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                        aria-label={`Forward requisition ${req._id}`}
                                                    >
                                                        <FaExchangeAlt className="mr-2" />
                                                        Forward
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12">
                                    <FiGrid className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No requisitions found</h3>
                                    <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria</p>
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FiChevronLeft className="mr-2 h-5 w-5" />
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                        <FiChevronRight className="ml-2 h-5 w-5" />
                                    </button>
                                </div>
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            Showing <span className="font-medium">{(currentPage - 1) * requisitionsPerPage + 1}</span> to{" "}
                                            <span className="font-medium">
                                                {Math.min(currentPage * requisitionsPerPage, filteredRequisitions.length)}
                                            </span>{" "}
                                            of <span className="font-medium">{filteredRequisitions.length}</span> results
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                            <button
                                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className="sr-only">Previous</span>
                                                <FiChevronLeft className="h-5 w-5" />
                                            </button>
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                const page = i + 1;
                                                // Show first page, current page, last page, and pages around current page
                                                if (
                                                    page === 1 ||
                                                    page === totalPages ||
                                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => setCurrentPage(page)}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                                currentPage === page
                                                                    ? "z-10 bg-orange-50 border-orange-500 text-orange-600"
                                                                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                                            }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                } else if (
                                                    (page === 2 && currentPage > 4) ||
                                                    (page === totalPages - 1 && currentPage < totalPages - 3)
                                                ) {
                                                    return (
                                                        <span
                                                            key={page}
                                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                                        >
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            })}
                                            <button
                                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className="sr-only">Next</span>
                                                <FiChevronRight className="h-5 w-5" />
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {selectedRequisition && (
                <ViewRequisitionModal
                    requisition={selectedRequisition}
                    onClose={() => setViewModalOpen(false)}
                    isOpen={viewModalOpen}
                />
            )}
        </div>
    );
}